import { useRef, useState, type FC } from "react";
import SideNav from "../nav/SideNav";
import { ComponentBaseContainer, ComponentContainer, JsonContainer, JsonHelperContainer, JsonTextContainer, StyledClearIcon, StyledCopyIcon } from "../jsonformatter/JsonFormatter.style";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-xml";
import Divider from "@mui/material/Divider";
import prettier from "prettier/standalone";
import parserHtml from "prettier/plugins/html";
import AdsComponent from "../AdsComponent";
import { Helmet } from "react-helmet-async";

const HtmlXmlFormatter: FC = () => {

    const [outputCode, setOutputCode] = useState<string>('');
    const [showToast, setShowToast] = useState(false);
    const editorRef = useRef<any>(null);
    const outputRef = useRef<any>(null);

    const handleInput = async (value: string) => {
        try {
            if (value === '') {
                setOutputCode('');
            } else {
                const formatted = await prettier.format(value, {
                    parser: "html",
                    plugins: [parserHtml],
                    htmlWhitespaceSensitivity: "ignore",
                });
                setOutputCode(formatted);
            }
        } catch (err) {
            setOutputCode(String(err));
        }
    };

    const handleClear = () => {
        editorRef.current?.editor?.setValue("", -1);
    };

    const handleCopy = () => {
        const content = outputRef.current?.editor?.getValue();
        content && navigator.clipboard.writeText(content)
            .then(() => {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 1000);
            });
    };

    return (
        <ComponentBaseContainer>
            <Helmet>
                <title>HTML/XML Formatter Online | Beautify & Minify Code</title>
                <meta name="description" content="Format and beautify your HTML or XML with this free online tool. Supports minification and auto-indentation for clean code." />
                <meta name="keywords" content="html formatter, xml formatter, beautify html, minify xml, format code online" />
            </Helmet>

            <SideNav />
            <ComponentContainer>
                <JsonContainer>
                    <JsonHelperContainer onClick={handleClear}>
                        <StyledClearIcon />
                    </JsonHelperContainer>
                    <Divider />
                    <JsonTextContainer
                        placeholder="Start typing (or) Paste here..."
                        ref={editorRef}
                        mode="html"
                        name="html-xml-editor"
                        wrapEnabled
                        onChange={handleInput}
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
                <Divider orientation="vertical" />
                <JsonContainer>
                    <JsonHelperContainer onClick={handleCopy}>
                        <StyledCopyIcon />
                    </JsonHelperContainer>
                    <Divider />
                    <JsonTextContainer
                        placeholder="Your output will appear here..."
                        ref={outputRef}
                        mode="html"
                        name="html-xml-output"
                        value={outputCode}
                        wrapEnabled
                        readOnly
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
            </ComponentContainer>
            {showToast && (
                <div style={{
                    position: "absolute",
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#323232",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    opacity: 0.9,
                    fontSize: "14px",
                }}>
                    Copied to clipboard!
                </div>
            )}
            <AdsComponent />
        </ComponentBaseContainer>
    );
};

export default HtmlXmlFormatter;
